import { cn } from '../lib/utils';

/** An option given with its own label, and the language that label is in. */
export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  /** Set when the label is in another language, e.g. "العربية" as `ar`. */
  lang?: string;
}

/**
 * How big the switch is. `default` matches the 48px toolbar controls it sits
 * beside in the admin; `compact` is for a corner of something else, like a
 * dialog header, where a full control height would outweigh what it switches.
 */
type Size = 'default' | 'compact';

const sizes: Record<Size, { group: string; button: string }> = {
  default: { group: 'h-(--target-control) rounded-xl', button: 'px-4 text-base font-semibold' },
  compact: { group: 'h-7 rounded-button', button: 'px-2.5 text-sm font-normal' },
};

/**
 * A two-or-more way switch: list/calendar, English/Arabic.
 *
 * Options are plain strings when the value reads well as its own label
 * (`['list', 'calendar']`, capitalised here), or `{ value, label }` when it does
 * not.
 */
export default function SegmentedToggle<T extends string>({
  value,
  options,
  onChange,
  size = 'default',
  'aria-label': ariaLabel,
  className,
}: {
  value: T;
  options: readonly T[] | readonly SegmentedOption<T>[];
  onChange: (next: T) => void;
  size?: Size;
  /** What the switch switches, for a screen reader: "Language". */
  'aria-label'?: string;
  className?: string;
}) {
  const items: SegmentedOption<T>[] = options.map((option) =>
    typeof option === 'string' ? { value: option, label: option } : option,
  );

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn(
        'flex shrink-0 overflow-hidden border border-hairline',
        sizes[size].group,
        className,
      )}
    >
      {items.map((item) => (
        <button
          key={item.value}
          type="button"
          lang={item.lang}
          onClick={() => onChange(item.value)}
          aria-pressed={value === item.value}
          className={cn(
            'capitalize transition-colors focus-visible:outline-(length:--focus-ring-width) focus-visible:-outline-offset-[3px] focus-visible:outline-(color:--focus-ring-color)',
            sizes[size].button,
            value === item.value
              ? 'bg-cell-selected-bg text-cell-selected-fg'
              : 'bg-surface text-ink-muted hover:text-brand-text',
          )}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}
